import InputField from "components/fields/InputField";
import Checkbox from "components/checkbox";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { api } from "../../services/hrms.api";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

export default function SignIn() {
  const nav = useNavigate();
  const { signin } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tenant, setTenant] = useState(
    localStorage.getItem("tenant") || "demo"
  );
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Privilege check: owner/admin/manager/superadmin → admin area, else employee dashboard
  const isPrivileged = (user) => {
    const role = String(
      user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "") || ""
    ).toLowerCase();
    return ["owner", "admin", "manager", "superadmin"].includes(role);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!tenant?.trim()) {
      setErr("Company ID is required");
      return;
    }
    if (!email?.trim() || !password?.trim()) {
      setErr("Email and password are required");
      return;
    }

    try {
      setLoading(true);

      // Save tenant locally
      localStorage.setItem("tenant", tenant.trim());
      // Login (send tenant header)
      const { data } = await api.post(
        "/auth/login",
        {
          email: email.trim(),
          password: password.trim(),
        },
        {
          headers: {
            "x-tenant-id": tenant.trim(),
          },
        }
      );

      // Session storage for non-remember
      if (!remember) {
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("user", JSON.stringify(data.user));
      }

      // Update auth context
      signin(data.token, data.user);

      // 🚀 Role-based redirect
      const dest = isPrivileged(data.user)
        ? "/admin/default"
        : "/employee/dashboard";
      nav(dest, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.message || "Login failed";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <form
          onSubmit={onSubmit}
          className="mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]"
        >
          <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
            Sign In
          </h4>
          <p className="mb-9 ml-1 text-base text-gray-600">
            Enter your credentials and <b>Company ID</b> to sign in.
          </p>

          {/* Tenant Input */}
          <InputField
            variant="auth"
            extra="mb-3"
            label="Company ID*"
            placeholder="e.g., demo, stado, tenant002"
            id="tenant"
            type="text"
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
          />

          {/* Email */}
          <InputField
            variant="auth"
            extra="mb-3"
            label="Email*"
            placeholder="owner@demo.com"
            id="email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {/* Password */}
          <div className="mb-3 w-full">
            <label
              htmlFor="password"
              className="mb-2 block text-sm font-medium text-navy-700 dark:text-white"
            >
              Password*
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Min. 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-sm outline-none transition focus:border-brand-400 dark:border-navy-700 dark:bg-navy-900 dark:text-white"
                autoComplete="current-password"
              />

              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-lg hover:opacity-90 dark:text-white"
              >
                {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </button>
            </div>
          </div>

          {/* Error */}
          {err && (
            <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-600">
              {err}
            </div>
          )}

          {/* Remember me */}
          <div className="mb-4 flex items-center justify-between px-2">
            <div className="flex items-center">
              <Checkbox
                color="redis"
                checked={remember}
                onChange={() => setRemember((v) => !v)}
              />
              <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
                Keep me logged In
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="linear mt-2 w-full rounded-xl 
             bg-gradient-to-r from-[#ef145d] to-[#e3955e]
             py-[12px] text-base font-medium text-white
             transition duration-200
             hover:opacity-90 active:opacity-80
             disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
